import { useState } from 'react';

interface ServiceRegion {
  name: string;
  cities: string[];
  lat: number;
  lng: number;
}

const regions: ServiceRegion[] = [
  {
    name: 'Mid-Atlantic',
    cities: [
      'Baltimore',
      'Charlotte',
      'Virginia Beach',
      'Richmond',
      'Northern Virginia',
      'Washington DC',
      'Philadelphia',
      'Lancaster',
      'Allentown',
      'New Castle',
      'Raleigh',
      'Greensboro',
    ],
    lat: 37.54,
    lng: -77.44,
  },
  {
    name: 'Northeast',
    cities: ['Maine', 'New England', 'New Jersey'],
    lat: 43.66,
    lng: -70.26,
  },
  {
    name: 'Southeast',
    cities: ['Nashville', 'Atlanta', 'Orlando', 'Tampa', 'Birmingham'],
    lat: 33.75,
    lng: -84.39,
  },
  {
    name: 'South Central',
    cities: ['Dallas', 'Arlington', 'Austin', 'St. Louis', 'Kansas City'],
    lat: 32.78,
    lng: -96.8,
  },
  {
    name: 'Midwest',
    cities: [
      'Columbus',
      'Cincinnati',
      'Lexington',
      'Louisville',
      'Northeast Ohio',
      'Pittsburgh',
      'Detroit',
    ],
    lat: 39.96,
    lng: -82.99,
  },
  {
    name: 'North Central',
    cities: ['Indianapolis', 'Minneapolis', 'Milwaukee', 'Madison', 'Chicago'],
    lat: 41.88,
    lng: -87.63,
  },
  {
    name: 'Western',
    cities: [
      'Billings',
      'Boise',
      'Denver',
      'Portland',
      'Sacramento',
      'Utah',
      'Seattle',
      'Spokane',
    ],
    lat: 43.62,
    lng: -116.21,
  },
];

export default function ServiceAreaMap() {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  return (
    <section id="service-areas" className="py-20 lg:py-28 bg-softbg">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-calm/10 rounded-full mb-5">
            <i className="ri-map-pin-line text-calm text-base"></i>
            <span className="text-calm text-sm font-semibold tracking-wide uppercase">
              Nationwide Coverage
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-ink mb-4">
            Serving Homeowners <span className="text-calm">Coast to Coast</span>
          </h2>
          <p className="text-muted text-base max-w-[600px] mx-auto leading-relaxed">
            SnapWindows proudly serves homeowners across the country. Find your region below and
            get started with your window replacement project.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Map */}
          <div className="w-full rounded-2xl overflow-hidden shadow-lg shadow-black/8 border border-border/60">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12800000!2d-98.5795!3d39.8283!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f5!3m3!1m2!1s0x54eab584e432360b%3A0x1c3bb99243deb742!2sUnited%20States!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="520"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="SnapWindows Service Areas across the United States"
              className="w-full"
            />
          </div>

          {/* Region List */}
          <div className="space-y-3">
            {regions.map((region) => (
              <div
                key={region.name}
                className={`rounded-xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                  activeRegion === region.name
                    ? 'bg-white border-calm/30 shadow-md shadow-calm/8'
                    : 'bg-white border-border/60 hover:border-calm/20 hover:shadow-sm'
                }`}
                onClick={() =>
                  setActiveRegion(activeRegion === region.name ? null : region.name)
                }
              >
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                        activeRegion === region.name ? 'bg-calm text-white' : 'bg-calm/10 text-calm'
                      }`}
                    >
                      <i className="ri-map-pin-2-fill text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-[15px] font-semibold text-ink">{region.name}</h4>
                      <p className="text-xs text-muted">{region.cities.length} locations</p>
                    </div>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-xl text-muted transition-transform duration-300 ${
                      activeRegion === region.name ? 'rotate-180 text-calm' : ''
                    }`}
                  ></i>
                </div>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    activeRegion === region.name ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 pb-4 pt-1">
                    <div className="flex flex-wrap gap-2">
                      {region.cities.map((city) => (
                        <span
                          key={city}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-softbg rounded-full text-sm text-ink font-medium"
                        >
                          <i className="ri-checkbox-circle-fill text-calm text-xs"></i>
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className="mt-6 p-5 bg-gradient-to-r from-teal to-calm rounded-xl text-white">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-question-line text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[15px] mb-1">
                    {'Don\u2019t see your area?'}
                  </h4>
                  <p className="text-white/85 text-sm leading-relaxed">
                    {'We\u2019re expanding fast. Reach out and we’ll let you know when we’re in your neighborhood.'}
                  </p>
                </div>
                <a
                  href="mailto:Snapwindowsai@gmail.com"
                  className="px-5 py-2.5 bg-white text-teal text-sm font-semibold rounded-full hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
